import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    MapPin, Clock, Users, Calendar, Star, Check, ChevronLeft, Share2, Heart, Send
} from 'lucide-react'
import { useLanguageStore, useCurrencyStore, useAuthStore } from '../../lib/store'
import { formatPrice } from '../../lib/utils'
import { toursApi } from '../../lib/api'

interface Tour {
    id: number
    type: string
    price: number
    duration: string
    location: string
    maxParticipants: number
    status: string
    title_en?: string
    title_vi?: string
    description_en?: string
    description_vi?: string
    highlights_en?: string
    highlights_vi?: string
    includes_en?: string
    includes_vi?: string
}

interface Review {
    id: number
    rating: number
    comment: string
    guest_name?: string
    user_name?: string
    created_at: string
}

export default function TourDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    useTranslation()
    const { language } = useLanguageStore()
    const { currency } = useCurrencyStore()
    const { user } = useAuthStore()

    const today = new Date().toISOString().split('T')[0]
    const [tour, setTour] = useState<Tour | null>(null)
    const [loading, setLoading] = useState(true)
    const [selectedDate, setSelectedDate] = useState(today)
    const [participants, setParticipants] = useState(1)

    // Reviews state
    const [reviews, setReviews] = useState<Review[]>([])
    const [reviewStats, setReviewStats] = useState({ average: 0, total: 0 })
    const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: 5, comment: '' })
    const [submitting, setSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    useEffect(() => {
        const fetchTour = async () => {
            if (!id) return
            setLoading(true)
            try {
                const response = await toursApi.get(parseInt(id))
                if (response.data?.success && response.data?.data) {
                    setTour(response.data.data)
                } else if (response.data && !('success' in response.data)) {
                    setTour(response.data)
                }

                // Fetch reviews
                const reviewsRes = await fetch(`/api/reviews/tour/${id}`)
                const reviewsData = await reviewsRes.json()
                if (reviewsData.success) {
                    setReviews(reviewsData.reviews || [])
                    setReviewStats(reviewsData.stats || { average: 0, total: 0 })
                }
            } catch (error) {
                console.error('Failed to fetch tour:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchTour()
    }, [id])

    const handleSubmitReview = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!id || !reviewForm.comment.trim()) return

        setSubmitting(true)
        try {
            const res = await fetch(`/api/reviews/tour/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    rating: reviewForm.rating,
                    comment: reviewForm.comment,
                    guest_name: user?.fullName || reviewForm.name,
                    guest_email: user?.email || reviewForm.email,
                    user_id: user?.id
                })
            })
            const data = await res.json()
            if (data.success) {
                setSubmitSuccess(true)
                setReviewForm({ name: '', email: '', rating: 5, comment: '' })
                setTimeout(() => setSubmitSuccess(false), 3000)
            }
        } catch (error) {
            console.error('Submit review error:', error)
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-pulse text-center">
                    <div className="w-16 h-16 bg-primary-200 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-500">{language === 'en' ? 'Loading...' : 'Đang tải...'}</p>
                </div>
            </div>
        )
    }

    if (!tour) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <h1 className="text-2xl font-bold mb-4">{language === 'en' ? 'Tour Not Found' : 'Không tìm thấy tour'}</h1>
                <Link to="/tours" className="text-primary-600 hover:underline">
                    {language === 'en' ? 'Back to Tours' : 'Quay lại trang Tours'}
                </Link>
            </div>
        )
    }

    const title = language === 'en' ? tour.title_en : (tour.title_vi || tour.title_en) || 'Tour'
    const description = language === 'en' ? tour.description_en : (tour.description_vi || tour.description_en) || ''
    const highlightsStr = language === 'en' ? tour.highlights_en : (tour.highlights_vi || tour.highlights_en) || ''
    const includesStr = language === 'en' ? tour.includes_en : (tour.includes_vi || tour.includes_en) || ''
    const highlights = highlightsStr ? highlightsStr.split('|').filter(Boolean) : []
    const includes = includesStr ? includesStr.split('|').filter(Boolean) : []
    const tourPrice = tour.price || 0
    const totalPrice = tourPrice * participants

    const handleBook = () => {
        if (!selectedDate) {
            alert(language === 'en' ? 'Please select a date' : 'Vui lòng chọn ngày')
            return
        }
        alert(language === 'en' ? 'Booking feature coming soon!' : 'Tính năng đặt tour sắp ra mắt!')
    }

    const renderStars = (rating: number, interactive = false, onChange?: (r: number) => void) => (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(star => (
                <button
                    key={star}
                    type="button"
                    onClick={() => interactive && onChange?.(star)}
                    className={interactive ? 'cursor-pointer' : 'cursor-default'}
                    disabled={!interactive}
                >
                    <Star className={`w-5 h-5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                </button>
            ))}
        </div>
    )

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero */}
            <div className="relative h-[40vh] md:h-[50vh] bg-gradient-to-br from-primary-400 via-primary-500 to-orange-400">
                <div className="absolute inset-0 bg-black/30" />
                <button
                    onClick={() => navigate(-1)}
                    className="absolute top-4 left-4 p-2 bg-white/20 backdrop-blur rounded-full hover:bg-white/40 transition z-10"
                >
                    <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <div className="absolute top-4 right-4 flex gap-2 z-10">
                    <button className="p-2 bg-white/20 backdrop-blur rounded-full hover:bg-white/40 transition">
                        <Share2 className="w-5 h-5 text-white" />
                    </button>
                    <button className="p-2 bg-white/20 backdrop-blur rounded-full hover:bg-white/40 transition">
                        <Heart className="w-5 h-5 text-white" />
                    </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="container mx-auto">
                        <span className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-sm mb-4 inline-block">
                            {tour.type === 'tour' ? '🗺️ Tour' : '🎟️ Ticket'}
                        </span>
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">{title}</h1>
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {tour.location}
                            </span>
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {tour.duration}
                            </span>
                            <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {language === 'en' ? `Up to ${tour.maxParticipants || 10}` : `Tối đa ${tour.maxParticipants || 10}`}
                            </span>
                            {reviewStats.total > 0 && (
                                <span className="flex items-center gap-1">
                                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                    {reviewStats.average.toFixed(1)} ({reviewStats.total})
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Description */}
                        <div className="bg-white rounded-xl p-6 border">
                            <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'About' : 'Giới thiệu'}</h2>
                            <p className="text-gray-600 leading-relaxed">{description}</p>
                        </div>

                        {/* Highlights */}
                        {highlights.length > 0 && (
                            <div className="bg-white rounded-xl p-6 border">
                                <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'Highlights' : 'Điểm nổi bật'}</h2>
                                <div className="grid grid-cols-2 gap-3">
                                    {highlights.map((item: string, i: number) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Includes/Excludes */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {includes.length > 0 && (
                                <div className="bg-white rounded-xl p-6 border">
                                    <h3 className="font-bold mb-3 text-green-600">{language === 'en' ? 'Included' : 'Bao gồm'}</h3>
                                    <ul className="space-y-2">
                                        {includes.map((item: string, i: number) => (
                                            <li key={i} className="flex items-center gap-2 text-sm">
                                                <Check className="w-4 h-4 text-green-500" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Reviews Section */}
                        <div className="bg-white rounded-xl p-6 border">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <Star className="w-5 h-5 text-yellow-500" />
                                    {language === 'en' ? 'Reviews' : 'Đánh giá'}
                                    {reviewStats.total > 0 && (
                                        <span className="text-sm font-normal text-gray-500">
                                            ({reviewStats.average.toFixed(1)} / 5 - {reviewStats.total} {language === 'en' ? 'reviews' : 'đánh giá'})
                                        </span>
                                    )}
                                </h2>
                            </div>

                            {/* Review Form */}
                            <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold mb-3">{language === 'en' ? 'Write a Review' : 'Viết đánh giá'}</h3>

                                {!user && (
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <input
                                            type="text"
                                            placeholder={language === 'en' ? 'Your name' : 'Tên của bạn'}
                                            value={reviewForm.name}
                                            onChange={e => setReviewForm({ ...reviewForm, name: e.target.value })}
                                            className="px-3 py-2 border rounded-lg text-sm"
                                            required
                                        />
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={reviewForm.email}
                                            onChange={e => setReviewForm({ ...reviewForm, email: e.target.value })}
                                            className="px-3 py-2 border rounded-lg text-sm"
                                            required
                                        />
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="block text-sm text-gray-600 mb-1">{language === 'en' ? 'Your rating' : 'Đánh giá của bạn'}</label>
                                    {renderStars(reviewForm.rating, true, (r) => setReviewForm({ ...reviewForm, rating: r }))}
                                </div>

                                <div className="flex gap-2">
                                    <textarea
                                        placeholder={language === 'en' ? 'Share your experience...' : 'Chia sẻ trải nghiệm của bạn...'}
                                        value={reviewForm.comment}
                                        onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                                        className="flex-1 px-4 py-2 border rounded-lg text-sm resize-none"
                                        rows={3}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="mt-3 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    <Send className="w-4 h-4" />
                                    {language === 'en' ? 'Submit Review' : 'Gửi đánh giá'}
                                </button>
                                {submitSuccess && (
                                    <p className="text-green-600 text-sm mt-2">
                                        {language === 'en' ? 'Review submitted for approval!' : 'Đánh giá đã gửi để duyệt!'}
                                    </p>
                                )}
                            </form>

                            {/* Reviews List */}
                            {reviews.length > 0 ? (
                                <div className="space-y-4">
                                    {reviews.map(review => (
                                        <div key={review.id} className="border-b pb-4 last:border-0">
                                            <div className="flex gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                                                    {(review.user_name || review.guest_name || 'A')[0].toUpperCase()}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-semibold text-sm">{review.user_name || review.guest_name}</span>
                                                        {renderStars(review.rating)}
                                                        <span className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-700">{review.comment}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-400 text-sm">
                                    {language === 'en' ? 'No reviews yet. Be the first!' : 'Chưa có đánh giá. Hãy là người đầu tiên!'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Booking Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl p-6 border sticky top-24">
                            <div className="text-center mb-6">
                                <p className="text-sm text-gray-500">{language === 'en' ? 'Price' : 'Giá'}</p>
                                <p className="text-3xl font-bold text-primary-600">
                                    {formatPrice(tourPrice, currency)}
                                </p>
                                <p className="text-sm text-gray-500">/ {language === 'en' ? 'person' : 'người'}</p>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        {language === 'en' ? 'Select Date' : 'Chọn ngày'}
                                    </label>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        <Users className="w-4 h-4 inline mr-1" />
                                        {language === 'en' ? 'Participants' : 'Số người'}
                                    </label>
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => setParticipants(Math.max(1, participants - 1))}
                                            className="w-10 h-10 border rounded-lg hover:bg-gray-50"
                                        >-</button>
                                        <span className="text-xl font-bold">{participants}</span>
                                        <button
                                            onClick={() => setParticipants(Math.min(tour.maxParticipants || 20, participants + 1))}
                                            className="w-10 h-10 border rounded-lg hover:bg-gray-50"
                                        >+</button>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4 mb-4">
                                <div className="flex justify-between text-lg font-bold">
                                    <span>{language === 'en' ? 'Total' : 'Tổng cộng'}</span>
                                    <span className="text-primary-600">{formatPrice(totalPrice, currency)}</span>
                                </div>
                            </div>

                            <button
                                onClick={handleBook}
                                className="w-full py-3 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 transition"
                            >
                                {language === 'en' ? 'Book Now' : 'Đặt ngay'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
